import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterMovies, sortMovies, type MovieFilter, type SortKey } from "@/utils/movie";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 12;

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "updated", label: "按更新", icon: "ri-time-line" },
  { key: "hot", label: "按热度", icon: "ri-fire-line" },
  { key: "rating", label: "按评分", icon: "ri-star-line" },
  { key: "year", label: "按年份", icon: "ri-calendar-line" },
];

const categoryIntro: Record<string, string> = {
  电影: "海量院线大片、口碑佳作与经典电影，涵盖剧情、科幻、动作、悬疑、爱情等多种题材。",
  电视剧: "热播剧集、经典长剧与海外佳作，更新及时，类型丰富，追剧不迷路。",
  全部: "汇集电影与电视剧全量内容，支持按类型、地区、年份、题材多维筛选，快速定位想看的内容。",
};

export default function CategoryPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const type = decodeURIComponent(params.type || "全部");
  const pathFilter = params.filter ? decodeURIComponent(params.filter) : undefined;

  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const genre = searchParams.get("genre") || "全部";
  const sort = (searchParams.get("sort") as SortKey) || "updated";
  const page = Math.max(1, Number(searchParams.get("page") || 1));

  usePageMeta({
    title: `${type} - 大师兄影视`,
    description: `大师兄影视${type}频道，${categoryIntro[type] || "汇聚最新影视资源，支持多维筛选与浏览。"}`,
    keywords: `${type},大师兄影视,${type}大全,${type}推荐,影视资源`,
  });

  // Optional path filter: treat as region when it matches a known region, otherwise as year.
  const pathRegion = pathFilter && ["华语", "欧美", "日韩", "其他"].includes(pathFilter) ? pathFilter : "全部";
  const pathYear = pathFilter && !["华语", "欧美", "日韩", "其他"].includes(pathFilter) ? pathFilter : "全部";

  const effectiveFilter: MovieFilter = {
    type,
    region: region !== "全部" ? region : pathRegion,
    year: year !== "全部" ? year : pathYear,
    genre,
  };

  const filtered = filterMovies(effectiveFilter);
  const sorted = sortMovies(filtered, sort);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hasActive =
    effectiveFilter.region !== "全部" ||
    effectiveFilter.year !== "全部" ||
    effectiveFilter.genre !== "全部";

  const updateParams = (updates: Record<string, string>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (!v || v === "全部") next.delete(k);
      else next.set(k, v);
    });
    if (resetPage) next.delete("page");
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (key: keyof MovieFilter, value: string) => {
    if (key === "type") {
      const qs = searchParams.toString();
      navigate(`/category/${encodeURIComponent(value)}${qs ? `?${qs}` : ""}`);
      return;
    }
    updateParams({ [key]: value });
  };

  const handleClear = () => {
    setSearchParams({}, { replace: true });
    if (pathFilter) navigate(`/category/${encodeURIComponent(type)}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageChange = (p: number) => {
    updateParams({ page: String(p) }, false);
  };

  const whiteList = ["电影", "电视剧", "全部"];
  const safeType = whiteList.includes(type) ? type : "全部";

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-600 mb-4" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/category/全部" className="hover:text-primary-600 transition-colors cursor-pointer">影视分类</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-950 font-medium">{type}</span>
        </nav>

        <div className="rounded-lg bg-gradient-to-r from-primary-500 to-primary-700 px-5 md:px-8 py-6 md:py-8">
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-background-50">
            {type}
            <span className="text-accent-400">频道</span>
          </h1>
          <p className="mt-2 text-sm text-background-100/85 max-w-2xl leading-6">{categoryIntro[type] || categoryIntro.全部}</p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8 flex flex-col lg:flex-row gap-6">
        <FilterPanel
          filter={{ ...effectiveFilter, type: safeType }}
          onChange={handleFilterChange}
          onClear={handleClear}
          hasActive={hasActive}
        />

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              共找到 <span className="text-primary-600 font-semibold">{sorted.length}</span> 部影视作品
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-full sm:w-auto overflow-x-auto hide-scrollbar">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => updateParams({ sort: opt.key })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    sort === opt.key
                      ? "bg-primary-500 text-background-50 font-semibold"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  <i className={opt.icon} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                {paged.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-center mb-4">
                  <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
                </div>
                <p className="text-center text-xs text-foreground-500">
                  第 {currentPage} / {totalPages} 页
                </p>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 rounded-lg bg-background-50 border border-background-200">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                <i className="ri-inbox-archive-line text-3xl text-secondary-600" />
              </span>
              <h3 className="font-heading text-lg font-bold text-foreground-950">暂无符合条件的影视作品</h3>
              <p className="mt-1.5 text-sm text-foreground-600">试试调整或清除筛选条件，发现更多精彩内容</p>
              <button
                type="button"
                onClick={handleClear}
                className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" />
                清除筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}